/**
 * lasHeader.js — parse a LAS/LAZ public header + VLRs in the browser and
 * detect what coordinate units the file is in, so the upload flow can decide
 * whether a file needs reprojecting to the project's meters CRS.
 *
 * The LAS public header block and the VLRs that follow it are UNCOMPRESSED
 * even in a .laz file (only the point records are LASzip-compressed), so we can
 * read everything we need from the first ~64 KB via `file.slice(...)`.
 *
 * This detection is advisory: it decides "already meters / needs reproject /
 * ask the user". The Reproject service re-resolves the source CRS
 * authoritatively server-side before transforming (see reproject_las.py).
 *
 * Refs: ASPRS LAS 1.4 spec (public header block + VLR layout); OGC GeoTIFF
 * GeoKey ids. Mirrors the server's resolve_source_crs().
 */

// GeoTIFF GeoKey ids (subset we classify on).
const GT_MODEL_TYPE = 1024;        // 1=Projected 2=Geographic 3=Geocentric
const GEOGRAPHIC_TYPE = 2048;      // EPSG geographic CS code, or 32767 user-defined
const PROJECTED_CS_TYPE = 3072;    // EPSG projected CS code, or 32767 user-defined
const PROJ_LINEAR_UNITS = 3076;    // 9001=m 9002=intl-ft 9003=us-survey-ft
const VERTICAL_UNITS = 4099;

const USER_DEFINED = 32767;
const UNDEFINED = 0;

const LINEAR_UNIT_BY_CODE = { 9001: 'metre', 9002: 'foot', 9003: 'usFoot' };

const GEOKEY_DIRECTORY_RECORD_ID = 34735;
const WKT_RECORD_ID = 2112; // OGC Coordinate System WKT (LAS 1.4)

const HEADER_READ_BYTES = 64 * 1024;

function readChars(view, offset, length) {
  let s = '';
  for (let i = 0; i < length; i += 1) {
    const c = view.getUint8(offset + i);
    if (c === 0) break;
    s += String.fromCharCode(c);
  }
  return s;
}

/**
 * Parse the LAS public header block + VLR table from an ArrayBuffer (the first
 * tens of KB of the file). Returns null if the buffer isn't a LAS/LAZ file.
 */
export function parseLasHeader(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (arrayBuffer.byteLength < 227) return null;
  if (readChars(view, 0, 4) !== 'LASF') return null;

  const globalEncoding = view.getUint16(6, true);
  const header = {
    versionMajor: view.getUint8(24),
    versionMinor: view.getUint8(25),
    globalEncodingWkt: (globalEncoding & 0x10) !== 0,
    headerSize: view.getUint16(94, true),
    offsetToPointData: view.getUint32(96, true),
    numVlrs: view.getUint32(100, true),
    // LASzip flags compression by setting bit 7 of the point-format byte; mask
    // it off to get the real point-data record format (0–10).
    pointFormat: view.getUint8(104, true) & 0x3f,
    scales: [view.getFloat64(131, true), view.getFloat64(139, true), view.getFloat64(147, true)],
    offsets: [view.getFloat64(155, true), view.getFloat64(163, true), view.getFloat64(171, true)],
    maxs: [view.getFloat64(179, true), view.getFloat64(195, true), view.getFloat64(211, true)],
    mins: [view.getFloat64(187, true), view.getFloat64(203, true), view.getFloat64(219, true)],
    geoKeys: {},
    wkt: null,
    vlrs: [],
  };

  // Walk the VLR table. Each VLR has a 54-byte header followed by its payload.
  let pos = header.headerSize || 227;
  const limit = Math.min(arrayBuffer.byteLength, header.offsetToPointData || arrayBuffer.byteLength);
  for (let i = 0; i < header.numVlrs; i += 1) {
    if (pos + 54 > limit) break;
    const recordId = view.getUint16(pos + 18, true);
    const recordLen = view.getUint16(pos + 20, true);
    const payloadStart = pos + 54;
    if (payloadStart + recordLen > arrayBuffer.byteLength) break;
    header.vlrs.push({ recordId, recordLen, payloadStart });

    if (recordId === GEOKEY_DIRECTORY_RECORD_ID) {
      header.geoKeys = parseGeoKeys(view, payloadStart, recordLen);
    } else if (recordId === WKT_RECORD_ID) {
      header.wkt = readChars(view, payloadStart, recordLen);
    }
    pos = payloadStart + recordLen;
  }
  return header;
}

/** Parse a GeoKeyDirectory VLR payload into { keyId: valueOffset } (inline keys only). */
function parseGeoKeys(view, start, len) {
  const out = {};
  if (len < 8) return out;
  const numKeys = view.getUint16(start + 6, true);
  for (let k = 0; k < numKeys; k += 1) {
    const e = start + 8 + k * 8;
    if (e + 8 > start + len) break;
    const keyId = view.getUint16(e, true);
    const tiffTagLocation = view.getUint16(e + 2, true);
    const valueOffset = view.getUint16(e + 6, true);
    // Only inline values (tiffTagLocation === 0) are meaningful here; values
    // stored in GeoDoubleParams/GeoAsciiParams aren't needed for unit/type.
    if (tiffTagLocation === 0) out[keyId] = valueOffset;
  }
  return out;
}

function linearUnitName(code) {
  if (code === undefined || code === UNDEFINED || code === USER_DEFINED) return null;
  return LINEAR_UNIT_BY_CODE[code] || null;
}

function looksGeographic(mins, maxs) {
  const [minX, minY] = mins;
  const [maxX, maxY] = maxs;
  return (
    minX >= -180 && maxX <= 180 && minY >= -90 && maxY <= 90 &&
    // guard against a degenerate all-zero header
    !(minX === 0 && maxX === 0 && minY === 0 && maxY === 0)
  );
}

/**
 * Decide the file's units and whether it needs reprojection.
 *
 * Returns:
 *   {
 *     kind: 'geographic' | 'projected' | 'unknown',
 *     linearUnit: 'metre' | 'foot' | 'usFoot' | null,
 *     angularUnit: 'degree' | null,
 *     verticalUnit: 'metre' | 'foot' | 'usFoot' | null,
 *     sourceCrs: { epsg } | { wkt } | null,   // best-guess source CRS
 *     needsReproject: boolean,
 *     canAuto: boolean,    // sourceCrs known → no modal needed
 *     reason: string,
 *   }
 *
 * Policy: the system assumes uploaded files are already in meters, so we only
 * flag a file when there's positive evidence it is NOT (degrees, or feet, or a
 * projected CRS whose unit is explicitly non-meter). Projected files with no
 * unit info are left alone (assumed meters — current behavior).
 */
export function detectUnits(header) {
  if (!header) {
    return {
      kind: 'unknown', linearUnit: null, angularUnit: null, verticalUnit: null,
      sourceCrs: null, needsReproject: false, canAuto: false,
      reason: 'Not a readable LAS/LAZ header',
    };
  }
  const gk = header.geoKeys || {};
  const model = gk[GT_MODEL_TYPE];
  const projUnit = linearUnitName(gk[PROJ_LINEAR_UNITS]);
  const vertUnit = linearUnitName(gk[VERTICAL_UNITS]);
  const rangeGeographic = looksGeographic(header.mins, header.maxs);

  // Geographic is determined by ModelType (2) or, when absent, the coordinate
  // range. NOT by GeogAngularUnits — a projected CRS legitimately carries that
  // key (degrees) for its underlying geographic base, so it can't distinguish
  // geographic from projected.
  const isGeographic = model === 2 || (model === undefined && rangeGeographic);

  if (isGeographic) {
    const geoEpsg = gk[GEOGRAPHIC_TYPE];
    const sourceCrs = (typeof geoEpsg === 'number' && geoEpsg !== UNDEFINED && geoEpsg !== USER_DEFINED)
      ? { epsg: geoEpsg }
      : { epsg: 4326 }; // user-defined / unspecified geographic → assume WGS84
    return {
      kind: 'geographic',
      linearUnit: null,
      angularUnit: 'degree',
      verticalUnit: vertUnit || 'metre',
      sourceCrs,
      needsReproject: true,
      canAuto: true,
      reason: `Geographic coordinates (degrees) → EPSG:${sourceCrs.epsg}`,
    };
  }

  const isProjected = model === 1 || (!rangeGeographic && (header.mins[0] !== 0 || header.maxs[0] !== 0));
  if (isProjected) {
    // Explicit non-meter unit → must reproject.
    if (projUnit === 'foot' || projUnit === 'usFoot') {
      const projEpsg = gk[PROJECTED_CS_TYPE];
      const sourceCrs = (typeof projEpsg === 'number' && projEpsg !== UNDEFINED && projEpsg !== USER_DEFINED)
        ? { epsg: projEpsg }
        : (header.wkt ? { wkt: header.wkt } : null);
      return {
        kind: 'projected',
        linearUnit: projUnit,
        angularUnit: null,
        verticalUnit: vertUnit || projUnit,
        sourceCrs,
        needsReproject: true,
        canAuto: !!sourceCrs,
        reason: sourceCrs
          ? `Projected, units = ${projUnit}`
          : `Projected, units = ${projUnit}, but source CRS is undefined`,
      };
    }
    // Explicit meters, or no unit info (assume meters — current behavior).
    return {
      kind: 'projected',
      linearUnit: projUnit || 'metre',
      angularUnit: null,
      verticalUnit: vertUnit || 'metre',
      sourceCrs: null,
      needsReproject: false,
      canAuto: false,
      reason: projUnit === 'metre' ? 'Projected, units = meters' : 'Projected, assumed meters (no unit tag)',
    };
  }

  return {
    kind: 'unknown', linearUnit: null, angularUnit: null, verticalUnit: null,
    sourceCrs: null, needsReproject: false, canAuto: false,
    reason: 'Could not determine coordinate system; assuming meters',
  };
}

/** Read just the header bytes from a File/Blob and parse them. */
export async function parseLasHeaderFromFile(file) {
  const blob = file.slice(0, HEADER_READ_BYTES);
  const buf = await blob.arrayBuffer();
  return parseLasHeader(buf);
}

/** Convenience: detect units directly from a File/Blob. */
export async function detectUnitsFromFile(file) {
  const header = await parseLasHeaderFromFile(file);
  return detectUnits(header);
}

/** True for `.las` / `.laz` filenames (case-insensitive). */
export function isLasLaz(name) {
  const ext = (String(name || '').split('.').pop() || '').toLowerCase();
  return ext === 'las' || ext === 'laz';
}
